import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useSettingsContext } from '@/contexts/SettingsContext';
import { useUserRole } from '@/hooks/useUserRole';

interface AdminRouteProps {
  children: ReactNode;
  requireInternal?: boolean; // Requires is_internal flag (Hollywood Trucks staff)
}

/**
 * Route-level authorization wrapper for platform features
 *
 * Prevents unauthorized page rendering by checking permissions BEFORE component renders.
 * This eliminates layout shift from delayed authorization checks inside components.
 *
 * Usage:
 *   <Route path="notifications" element={
 *     <AdminRoute>
 *       <SettingsNotifications />
 *     </AdminRoute>
 *   } />
 */
export function AdminRoute({ children, requireInternal = true }: AdminRouteProps) {
  const { is_superadmin, user, loading } = useSettingsContext();
  const { isGroupAdmin } = useUserRole();

  // Show nothing while loading (prevents flash of unauthorized content)
  if (loading) {
    return null;
  }

  // Redirect if not authorized
  // Platform features require superadmin + internal status
  if (!is_superadmin || (requireInternal && !user?.is_internal) || isGroupAdmin) {
    return <Navigate to="/settings" replace />;
  }

  // Authorized - render children
  return <>{children}</>;
}
